import { __, sprintf } from "@wordpress/i18n";
import { useEffect, useState } from "@wordpress/element";
import { css } from "@/utils/css";
import { SwitchBox } from "@/components/switch";
import { ColorInput } from "@/components/color-input";
import { CHAT_CONFIG, CHATBOT_ICONS, CHATBOT_POSITIONS, HAS_EAZYDOCS_PRO, IS_PRO } from "@/utils/constants";
import { api } from "@/config/api-fetch";
import { toast } from "react-toastify";
import { TextInput } from "@/components/text-input";
import { TextareaInput } from "@/components/textarea-input";
import { Select } from "@/components/select";
import { FormBox } from "@/components/form-box";
import { Button } from "@/components/button";
import { CodeCopy } from "@/components/code-copy";
import { useConversations } from "@/hooks/use-conversations";
import { SimpleProAlert, useProModal } from "@/components/pro-alert-box";
import { useUsageStats } from "@/hooks/use-usage-stats";

type Props = {
    config: typeof CHAT_CONFIG;
    setConfig: React.Dispatch<React.SetStateAction<typeof CHAT_CONFIG>>;
};

export function ChatbotConfig({ config, setConfig }: Props) {
    const [saving, setSaving] = useState(false);
    const { data: conversations } = useConversations();
    const { showProAlert } = useProModal();

    const saveConfig = async () => {
        setSaving(true);

        const success = await api.post("/preferences/chatbot", config).catch(() => false);

        if (success) {
            toast.success(__("Configuration saved successfully.", "antimanual"));
        } else {
            toast.error(__("Failed to save configuration. Please try again later.", "antimanual"));
        }

        setSaving(false);
    };

    const generateEmbedCode = () => {
        const currentSiteUrl = window.location.href.split("/wp-admin")[0];
        const selectedIcon = CHATBOT_ICONS.find((icon) => icon.value === config.icon);
        const iconSvg = selectedIcon?.url
            ? `<img src="${selectedIcon.url}" style="filter: invert(97%) sepia(100%) saturate(13%) hue-rotate(196deg) brightness(103%) contrast(103%);" width="16px" alt="${selectedIcon.label}" />`
            : `<svg fill="${config.bg_color}" width="16px" class="antimanual-icon ea-robot" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
    <path d="M320 0c17.7 0 32 14.3 32 32l0 64 120 0c39.8 0 72 32.2 72 72l0 272c0 39.8-32.2 72-72 72l-304 0c-39.8 0-72-32.2-72-72l0-272c0-39.8 32.2-72 72-72l120 0 0-64c0-17.7 14.3-32 32-32zM208 384c-8.8 0-16 7.2-16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-32 0zm96 0c-8.8 0-16 7.2-16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-32 0zm96 0c-8.8 0-16 7.2-16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-32 0zM264 256a40 40 0 1 0 -80 0 40 40 0 1 0 80 0zm152 40a40 40 0 1 0 0-80 40 40 0 1 0 0 80zM48 224l16 0 0 192-16 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm544 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-16 0 0-192 16 0z"></path>
</svg>`;

        return `<div class="antimanual-cross-domain-code">
   <!-- Embed Assistant Styles -->
   <link rel="stylesheet" href="${currentSiteUrl}/wp-content/plugins/antimanual/assets/css/embed-assistant.css" media="all">
   <script src="${currentSiteUrl}/wp-content/plugins/antimanual/assets/js/embed-assistant.js"></script>
   <button class="antimanual-embeded-chat-toggle" style="background-color: ${config.primary_color};color: ${config.bg_color};">
     <div>
       <span class="help-icon">${iconSvg}</span>
       <span class="close-icon" style="display: none;">
         <svg fill="${config.bg_color}" width="16px" class="antimanual-icon ea-times" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
            <path d="M0 2 L14 16 L16 14 L2 0 L0 2Z M14 0 L0 14 L2 16 L16 2 L14 0Z"></path>
         </svg>
       </span>
       <span> ${config.btn_txt || "Help"} </span>
     </div>
   </button>
   <div class="antimanual-chatbox-iframe-wraper" >
      <iframe src="${currentSiteUrl}/antimanual-assistant/" style="border: none;" frameborder="0"></iframe>
   </div>
</div>`;
    };

    const { data: stats } = useUsageStats();
    const count = Number(stats?.chatbot.count || 0);
    const limit = Number(stats?.chatbot.limit || 60);
    const exceeded = !IS_PRO && count >= limit;

    useEffect(() => {
        if (exceeded && config.enabled) {
            setConfig((prev) => ({ ...prev, enabled: false }));
        }
    }, [config.enabled, exceeded]);

    return (
        <div className={css("p-8")}>
            {!IS_PRO && (
                <div className={css("mb-5")}>
                    <SimpleProAlert>{sprintf(__("Usage: %1$d / %2$d conversations this month", "antimanual"), count, limit)}</SimpleProAlert>
                </div>
            )}
            <div className={css("config")}>
                <div className={css("general")}>
                    <SwitchBox
                        className={css("mb-5")}
                        checked={!!config.enabled}
                        onChange={(checked) => {
                            if (exceeded && checked) {
                                showProAlert(
                                    __("Monthly Limit Exceeded", "antimanual") + ` (${count}/${limit})`,
                                    __(
                                        "You have exceeded the monthly limit of conversations. Upgrade to allow unlimited conversations.",
                                        "antimanual"
                                    )
                                );
                                return;
                            }
                            setConfig((prev) => ({ ...prev, enabled: checked }));
                        }}
                        label={__("Display Chatbot", "antimanual")}
                        help={__("Chatbot popup will be displayed in the frontend", "antimanual")}
                    />

                    {!!config.enabled && (
                        <>
                            {HAS_EAZYDOCS_PRO && (
                                <SwitchBox
                                    className={css("mb-5")}
                                    checked={!!config.merge_ezd}
                                    onChange={(checked) => setConfig((prev) => ({ ...prev, merge_ezd: checked }))}
                                    label={__("Merge with EazyDocs", "antimanual")}
                                    help={__("Will be added as a tab in EazyDocs assistant.", "antimanual")}
                                />
                            )}

                            <div className={css("grid grid-cols-2 gap-5 mb-6")}>
                                <TextInput
                                    label={__("Chatbot Title", "antimanual")}
                                    help={__("Name of the chatbot", "antimanual")}
                                    value={config.title}
                                    onChange={(value) => setConfig((prev) => ({ ...prev, title: value }))}
                                />
                                {!config.merge_ezd && (
                                    <TextInput
                                        value={config.btn_txt}
                                        onChange={(value) => setConfig((prev) => ({ ...prev, btn_txt: value }))}
                                        label={__("Chatbot Button Text", "antimanual")}
                                        help={__("The text displayed on the chatbot button", "antimanual")}
                                    />
                                )}
                            </div>

                            {HAS_EAZYDOCS_PRO && config.merge_ezd && (
                                <div className={css("grid grid-cols-2 gap-5 mb-6")}>
                                    <TextInput
                                        label={__("Chatbot Label", "antimanual")}
                                        help={__("Label of the chatbot's tab in EazyDocs assistant", "antimanual")}
                                        value={config.label}
                                        onChange={(value) => setConfig((prev) => ({ ...prev, label: value }))}
                                    />
                                    <Select
                                        label={__("Chatbot Position", "antimanual")}
                                        help={__("Position of the chatbot's tab in EazyDocs assistant", "antimanual")}
                                        value={config.position}
                                        onChange={(value) => setConfig((prev) => ({ ...prev, position: value }))}
                                        options={CHATBOT_POSITIONS}
                                    />
                                </div>
                            )}

                            <div className={css("grid grid-cols-2 gap-5 mb-6")}>
                                <ColorInput
                                    label={__("Primary Color", "antimanual")}
                                    help={__("The primary color of the chatbot", "antimanual")}
                                    defaultValue="#0066CC"
                                    value={config.primary_color}
                                    onChange={(color) => setConfig((prev) => ({ ...prev, primary_color: color }))}
                                />
                                <ColorInput
                                    label={__("Background Color", "antimanual")}
                                    help={__("The background color of the chatbot", "antimanual")}
                                    defaultValue="#f1f1f1"
                                    value={config.bg_color}
                                    onChange={(color) => setConfig((prev) => ({ ...prev, bg_color: color }))}
                                />
                            </div>

                            {!config.merge_ezd && (
                                <label>
                                    <span className={css("form-label")}>{__("Chat Icon", "antimanual")}</span>
                                    <div className={css("icon-grid")}>
                                        {CHATBOT_ICONS.map((icon) => (
                                            <div
                                                key={icon.value}
                                                className={css(`icon-option ${config.icon === icon.value ? "selected" : ""}`)}
                                                onClick={() => setConfig((prev) => ({ ...prev, icon: icon.value }))}
                                            >
                                                <img src={icon.url} alt={icon.label} />
                                                <h4>{icon.label}</h4>
                                            </div>
                                        ))}
                                    </div>
                                </label>
                            )}
                        </>
                    )}
                    {!!config.enabled && !config.merge_ezd && (
                        <CodeCopy
                            className={css("mt-6")}
                            value={generateEmbedCode()}
                            label={__("Cross Domain Embed", "antimanual")}
                            help={
                                <>
                                    {__(
                                        "Extend your AI chat bot to other websites by embedding it across multiple domains with a simple code snippet.",
                                        "antimanual"
                                    )}
                                    {!IS_PRO && (
                                        <h4 className={css("text-orange")}>
                                            {__(
                                                "Note: Cross Domain Embedding will not work since you are in the free version. Upgrade to Pro to use this feature.",
                                                "antimanual"
                                            )}
                                        </h4>
                                    )}
                                </>
                            }
                        />
                    )}
                </div>
                {!!config.enabled && (
                    <div className={css("messages")}>
                        <TextareaInput
                            label={__("Help Text", "antimanual")}
                            help={__("Description of the Chatbot", "antimanual")}
                            value={config.help_text}
                            onChange={(value) => setConfig((prev) => ({ ...prev, help_text: value }))}
                            placeholder={__("Enter help text...", "antimanual")}
                            className="mb-6"
                        />

                        <TextareaInput
                            label={__("Welcome Message", "antimanual")}
                            help={__("First message of the Chatbot to greet the user", "antimanual")}
                            value={config.wlc_msg}
                            onChange={(value) => setConfig((prev) => ({ ...prev, wlc_msg: value }))}
                            placeholder={__("Enter welcome message...", "antimanual")}
                            className="mb-6"
                        />

                        <TextareaInput
                            label={__("Irrelevant Query Reply", "antimanual")}
                            help={__("Message to display when the chatbot receives an irrelevant or out-of-scope query.", "antimanual")}
                            value={config.irrelevant_ans}
                            onChange={(value) => setConfig((prev) => ({ ...prev, irrelevant_ans: value }))}
                            placeholder={__("Enter irrelevant query reply...", "antimanual")}
                            className="mb-6"
                        />

                        <FormBox
                            className={css("flex flex-col gap-1 mb-6")}
                            label={__("Pre-built Messages", "antimanual")}
                            help={__("Pre-defined messages that users can click to send", "antimanual")}
                        >
                            <TextInput
                                value={config.prebuilt_1}
                                onChange={(value) =>
                                    setConfig((prev) => ({
                                        ...prev,
                                        prebuilt_1: value,
                                    }))
                                }
                                placeholder={__("Pre-built message 1", "antimanual")}
                            />
                            <TextInput
                                value={config.prebuilt_2}
                                onChange={(value) =>
                                    setConfig((prev) => ({
                                        ...prev,
                                        prebuilt_2: value,
                                    }))
                                }
                                placeholder={__("Pre-built message 2", "antimanual")}
                            />
                            <TextInput
                                value={config.prebuilt_3}
                                onChange={(value) =>
                                    setConfig((prev) => ({
                                        ...prev,
                                        prebuilt_3: value,
                                    }))
                                }
                                placeholder={__("Pre-built message 3", "antimanual")}
                            />
                        </FormBox>
                    </div>
                )}
            </div>
            <Button loading={saving} onClick={saveConfig} className={css("mt-6 mx-auto p-4 min-w-sm")}>
                {__("Save Configuration", "antimanual")}
            </Button>
        </div>
    );
}
